import React, { useState, useEffect, useCallback } from "react";
import { GameOverType, GTVMessage, PlayerType } from "../../shared/util/types";
import {
  AllGuessesInTypeTriviaLies,
  AllLiesInType,
  NewQuestionType,
  NextRoundType,
} from "./types";
import ShowScores from "../../shared/components/ShowScores";
import ShowCards from "../../shared/components/ShowCards";
import Instructions from "../../shared/components/Instructions";
import ResultPresentation from "./ResultPresentation";
import TransitionContainer from "../../shared/components/TransitionContainer";
import { DEFAULT_THEME } from "../../shared/util/consts";
import { Box } from "@mantine/core";
import DynamicText from "../../shared/components/DynamicText";
import PlayerReminder from "../../shared/components/PlayerReminder";

const TriviaLiesGame = () => {
  const delayShowingAnswersMS = 10000;
  const [players, setPlayers] = useState<PlayerType[]>([]);
  const theme = DEFAULT_THEME;
  const sendHostMessage = useCallback(async (msg: string) => {}, []);
  const [round, setRound] = useState(1);
  const [instructions, setInstructions] = useState("");
  const [reminder, setReminder] = useState("");
  const [state, setState] = useState("NextRound"); // Should be NextRound to start
  const [cards, setCards] = useState<string[]>([]);
  const [results, setResults] = useState<AllGuessesInTypeTriviaLies | null>(
    null,
  );
  const [gameOverData, setGameOverData] = useState<GameOverType | null>(null);

  const setPlayersUnsubmitted = useCallback(() => {
    setPlayers(
      players.map((p) => {
        return {
          ...p,
          Submitted: false,
        };
      }),
    );
  }, [players, setPlayers]);
  useEffect(() => {
    const onLoaded = async () => {
      await sendHostMessage("GameStarted");
    };
    onLoaded();
  }, [sendHostMessage]);
  const handleMessage = useCallback(
    async (msg: GTVMessage) => {
      console.log("TriviaLies Processing msg", msg);
      if (!msg) {
        return false;
      }
      switch (msg.Type) {
        case "AddPlayer":
          const newPlayer = msg.Payload as PlayerType;
          setPlayers([...players, { ...newPlayer, Submitted: true }]);
          break;
        case "NextRound":
          setState(msg.Type);
          setResults(null);
          const nextRound: NextRoundType = msg.Payload as NextRoundType;
          setRound(nextRound.Round);
          setInstructions(nextRound.Instructions);
          setReminder(nextRound.Reminder);
          setCards(nextRound.Categories);
          setPlayersUnsubmitted();
          await sendHostMessage("ShowInstructionsComplete");
          break;
        case "NewQuestion":
          setState(msg.Type);
          const newQuestion: NewQuestionType = msg.Payload as NewQuestionType;
          setInstructions(newQuestion.Question);
          setReminder(newQuestion.Reminder);
          setPlayersUnsubmitted();
          setCards([]);
          await sendHostMessage("ShowQuestionComplete");
          break;
        case "OnePlayerIn":
          setPlayers(
            players.map((p) => {
              return {
                ...p,
                Submitted:
                  p.Name === (msg.Payload as string) ? true : p.Submitted,
              };
            }),
          );
          break;
        case "AllLiesIn":
          setState(msg.Type);
          const allLiesIn: AllLiesInType = msg.Payload as AllLiesInType;
          setReminder(allLiesIn.Reminder);
          setCards(allLiesIn.AllOptions);
          await sendHostMessage("ShowLiesComplete");
          break;
        case "AllGuessesIn":
          setState(msg.Type);
          const results: AllGuessesInTypeTriviaLies =
            msg.Payload as AllGuessesInTypeTriviaLies;
          setReminder("");
          setInstructions("");
          setResults(results);
          await new Promise((resolve) =>
            setTimeout(resolve, delayShowingAnswersMS),
          );
          await sendHostMessage("ShowResultsComplete");
          break;
        case "GameOver":
          setState(msg.Type);
          const gameOver: GameOverType = msg.Payload as GameOverType;
          setGameOverData(gameOver);
          return true;
        default:
          console.log("Unimplemented message:", msg);
          break;
      }
      return false;
    },
    [players, setPlayers, setPlayersUnsubmitted, sendHostMessage],
  );
  // TODO
  useEffect(() => {
    handleMessage({ Type: "TestMessage", Payload: {} } as GTVMessage);
  }, [handleMessage]);
  return (
    <TransitionContainer initiallyVisible={true} transitionTrigger={state}>
      {state === "NextRound" || state === "NewQuestion" ? (
        <Instructions
          theme={theme}
          instructions={instructions}
          players={players}
          reminder={reminder}
          round={"Round " + round}
        />
      ) : state === "AllLiesIn" ? (
        <ShowCards
          theme={theme}
          instructions={instructions}
          players={players}
          reminder={reminder}
          round={"Round " + round}
          cards={cards}
        />
      ) : state === "AllGuessesIn" ? (
        <ResultPresentation
          theme={theme}
          hostMessage={sendHostMessage}
          round={"Round " + round}
          results={results || undefined}
        />
      ) : state === "GameOver" && gameOverData ? (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            height: "calc(95vh)",
          }}
        >
          <DynamicText style={{ fontSize: 30, marginBottom: 10 }}>
            Game Over!
          </DynamicText>
          <ShowScores
            players={players}
            onRenderComplete={async () => {
              await sendHostMessage("ShowResultsComplete");
            }}
          />
          <PlayerReminder reminder={reminder} theme={theme} />
        </Box>
      ) : null}
    </TransitionContainer>
  );
};

export default TriviaLiesGame;
